import {
  Activity,
  BarChart3,
  Boxes,
  ChevronRight,
  CreditCard,
  FileText,
  Home,
  Image,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  Palette,
  ReceiptText,
  Search,
  Settings,
  Shield,
  Truck,
  Users
} from "lucide-react";
import { Link, NavLink, Outlet } from "react-router-dom";

const groups = [
  { title: "Dashboard", items: [["Data Overview", "/admin/dashboard", LayoutDashboard]] },
  {
    title: "Homepage",
    items: [
      ["Sections", "/admin/homepage", Home],
      ["Banners", "/admin/banners", Image],
      ["Media", "/admin/media", Image]
    ]
  },
  {
    title: "Products",
    items: [
      ["All Products", "/admin/products", Package],
      ["Add Product", "/admin/products/new", Boxes],
      ["Categories", "/admin/categories", Boxes],
      ["Tags", "/admin/tags", Boxes]
    ]
  },
  {
    title: "Orders",
    items: [
      ["All Orders", "/admin/orders", ReceiptText],
      ["Pending Payment", "/admin/orders?status=pending_payment", ReceiptText],
      ["Payment Submitted", "/admin/orders?status=payment_submitted", ReceiptText],
      ["Paid Orders", "/admin/orders?status=paid", ReceiptText],
      ["Shipped Orders", "/admin/orders?status=shipped", Truck]
    ]
  },
  {
    title: "Payments",
    items: [
      ["Payment Methods", "/admin/payment-methods", CreditCard],
      ["Payment Reviews", "/admin/payment-reviews", CreditCard]
    ]
  },
  { title: "Customers", items: [["Customer List", "/admin/customers", Users]] },
  {
    title: "Content",
    items: [
      ["Pages", "/admin/pages", FileText],
      ["Policies", "/admin/policies", FileText]
    ]
  },
  {
    title: "Settings",
    items: [
      ["General", "/admin/settings/general", Settings],
      ["Languages", "/admin/settings/languages", Settings],
      ["Currency", "/admin/settings/currency", Settings],
      ["Theme", "/admin/settings/theme", Palette],
      ["SEO", "/admin/settings/seo", BarChart3],
      ["Pixel", "/admin/settings/pixel", Activity],
      ["Live Chat", "/admin/settings/live-chat", Users]
    ]
  },
  {
    title: "System",
    items: [
      ["Admin Users", "/admin/users", Shield],
      ["Activity Logs", "/admin/activity-logs", Activity],
      ["Export", "/admin/export", FileText]
    ]
  }
] as const;

export default function AdminLayout() {
  return (
    <div className="min-h-screen bg-[#F6F1E8] text-[#0F172A] lg:grid lg:grid-cols-[288px_1fr]">
      <aside className="sticky top-0 hidden h-screen overflow-y-auto border-r border-[#0F172A]/10 bg-white/78 px-4 py-5 shadow-[18px_0_45px_rgba(15,23,42,0.05)] backdrop-blur-2xl lg:block">
        <Link to="/admin/dashboard" className="mb-6 flex items-center gap-3 rounded-[24px] bg-[#0F172A] p-3 text-white shadow-[0_18px_40px_rgba(15,23,42,0.16)]">
          <img src="/logo.svg" className="h-10 rounded-2xl bg-white p-1" />
          <div>
            <p className="text-sm font-black">SellerSupply Shop</p>
            <p className="text-xs text-white/65">Single store admin</p>
          </div>
        </Link>
        <nav className="space-y-5">
          {groups.map((group) => (
            <div key={group.title}>
              <p className="mb-2 px-3 text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">{group.title}</p>
              <div className="space-y-1">
                {group.items.map(([label, to, Icon]) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      `flex items-center justify-between rounded-2xl px-3 py-2.5 text-sm font-semibold transition ${
                        isActive ? "bg-[#0F172A] text-white shadow-[0_12px_28px_rgba(15,23,42,0.14)]" : "text-slate-600 hover:bg-[#F7F3EC] hover:text-[#0F172A]"
                      }`
                    }
                  >
                    <span className="flex items-center gap-2"><Icon size={17} />{label}</span>
                    <ChevronRight size={14} className="opacity-45" />
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
      </aside>
      <main className="min-w-0">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-[#0F172A]/10 bg-white/72 px-4 backdrop-blur-2xl md:px-8">
          <div className="flex items-center gap-3">
            <button className="rounded-full border border-[#0F172A]/10 bg-white p-2 lg:hidden"><Menu size={20} /></button>
            <div className="hidden h-10 w-[320px] items-center gap-2 rounded-full border border-[#0F172A]/10 bg-white px-4 md:flex">
              <Search size={17} className="text-slate-400" />
              <span className="text-sm text-slate-400">Search orders, products, customers</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link className="btn-secondary hidden py-2 md:inline-flex" to="/en">View Store</Link>
            <div className="rounded-full bg-white px-4 py-2 text-sm font-bold shadow-[0_10px_28px_rgba(15,23,42,0.08)]">Super Admin</div>
            <button className="rounded-full border border-[#0F172A]/10 bg-white p-2"><LogOut size={18} /></button>
          </div>
        </header>
        <div className="p-4 md:p-8"><Outlet /></div>
      </main>
    </div>
  );
}
